/**
 * AURA Field Interaction — Unified activation mode.
 *
 * Replaces Create + Activate + Evolve with one cluster-linked Field.
 *
 * A Field is a live activation environment grounded in cluster intelligence.
 * It has three phases driven by status:
 *
 *   designing → Open Field → live → Close Field → complete
 *
 * Designing: name the field, get a brief from the cluster-agent, set participation model
 * Live:       real signal feed from the graph, adaptations, health state
 * Complete:   learnings browser, push to cluster-agent memory, evolve the cluster
 *
 * AURA Formula: SENSE → SHAPE → SHIFT → SUSTAIN
 */

import type { SyntheticField } from '../data/synthetic';
import { useAuraStore } from '../state/store';

const GATEWAY = import.meta.env.VITE_API_URL || '';

const PARTICIPATION_MODELS = [
  { id: 'immersive', label: 'Immersive Environment', description: 'People enter a world you built. High dwell time, high data capture, harder to scale.' },
  { id: 'sampling', label: 'Sampling Ecosystem', description: 'Product meets person at the moment of decision. Direct conversion signal, low friction.' },
  { id: 'content-first', label: 'Content-First Activation', description: 'Designed to be captured and spread. The activation is the content. Amplification is the measure.' },
  { id: 'community', label: 'Community Integration', description: 'Built with local culture, not imposed on it. Earns trust. Slower to activate, longer to echo.' },
  { id: 'talent', label: 'Talent Partnership', description: 'The creator IS the activation. Their audience is your audience. Velocity depends on the match.' },
  { id: 'hybrid', label: 'Digital-Physical Hybrid', description: 'Real-world triggers digital signal. Or digital drives physical action. The loop is the product.' },
];

const HEALTH_STATES = [
  { id: 'exploratory', label: 'Exploratory', color: '#22c55e', description: 'The field is learning. Keep it open. Let signals accumulate.' },
  { id: 'saturating', label: 'Saturating', color: '#f59e0b', description: 'Signal density is compressing. Decide: adapt the participation model, or start capturing learnings.' },
  { id: 'breaking', label: 'Breaking', color: '#ef4444', description: 'The field has peaked. Close it now and harvest what you learned before it fades.' },
];

function timeAgo(ts: number) {
  const s = Math.floor((Date.now() - ts) / 1000);
  if (s < 60) return `${s}s`;
  if (s < 3600) return `${Math.floor(s / 60)}m`;
  return `${Math.floor(s / 3600)}h`;
}

export class FieldInteraction {
  private panel: HTMLElement | null;
  private field: SyntheticField;

  // State
  private currentField: any = null;
  private currentCluster: any = null;
  private currentClusterNodeIndex: number = -1;
  private signals: any[] = [];
  private healthState = 'exploratory';
  private refreshTimer: any = null;

  constructor(field: SyntheticField) {
    this.field = field;
    this.panel = document.getElementById('field-panel');
    this.setupPanel();
  }

  private setupPanel() {
    if (!this.panel) return;
    this.panel.innerHTML = '';
    this.render();
  }

  // ── Called by the mode system when Field mode is entered ──────────────────

  onEnter(selectedClusterId?: string) {
    if (selectedClusterId) {
      this.loadCluster(selectedClusterId);
    } else {
      this.renderClusterPicker();
    }
  }

  onExit() {
    clearInterval(this.refreshTimer);
  }

  // ── Cluster loading ────────────────────────────────────────────────────────

  private async loadCluster(clusterId: string, nodeIndex?: number) {
    if (nodeIndex !== undefined) this.currentClusterNodeIndex = nodeIndex;
    try {
      const res = await fetch(`${GATEWAY}/api/clusters/${clusterId}`);
      if (!res.ok) return;
      const data = await res.json() as any;
      this.currentCluster = data.cluster;

      // Check for existing field on this cluster
      const fieldsRes = await fetch(`${GATEWAY}/api/fields?clusterId=${clusterId}`);
      if (fieldsRes.ok) {
        const fieldsData = await fieldsRes.json() as any;
        const activeField = (fieldsData.fields || []).find((f: any) => f.status !== 'complete');
        if (activeField) {
          this.currentField = activeField;
          if (activeField.status === 'live') this.startSignalRefresh();
        }
      }

      this.render();
    } catch (err) {
      console.warn('[FieldInteraction] loadCluster failed:', err);
    }
  }

  // ── Signal feed (live phase) ───────────────────────────────────────────────

  private async fetchSignals() {
    if (!this.currentCluster) return;
    try {
      const res = await fetch(`${GATEWAY}/api/clusters/${this.currentCluster.id}`);
      if (!res.ok) return;
      const data = await res.json() as any;
      this.signals = (data.signals || []).slice(0, 20);
      this.renderSignalFeed();
    } catch {}
  }

  private startSignalRefresh() {
    clearInterval(this.refreshTimer);
    this.fetchSignals();
    this.refreshTimer = setInterval(() => this.fetchSignals(), 30_000);
  }

  // ── Field lifecycle ────────────────────────────────────────────────────────

  private async openField(name: string, participationModel: string, brief: string) {
    if (!this.currentCluster) return;

    const btn = document.getElementById('fp-open-btn') as HTMLButtonElement | null;
    if (btn) { btn.textContent = 'Opening…'; btn.disabled = true; }

    try {
      const res = await fetch(`${GATEWAY}/api/fields`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          clusterId: this.currentCluster.id,
          name, brief, participationModel, constraints: [],
        }),
        signal: AbortSignal.timeout(10_000),
      });

      if (!res.ok) {
        const errText = await res.text().catch(() => '');
        this.showError(`Failed to open field (${res.status}). ${errText.slice(0, 80)}`);
        if (btn) { btn.textContent = 'Open Field →'; btn.disabled = false; }
        return;
      }

      this.currentField = await res.json();

      // Transition to live
      const patchRes = await fetch(`${GATEWAY}/api/fields/${this.currentField.id}/status`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: 'live', activationStart: Date.now() }),
        signal: AbortSignal.timeout(8_000),
      });

      this.currentField.status = 'live';
      this.currentField.activationStart = Date.now();
      this.startSignalRefresh();
      // Highlight the active cluster in the 3D field
      useAuraStore.getState().setActiveFieldCluster(this.currentClusterNodeIndex);
      this.render();

    } catch (err: any) {
      console.error('[FieldInteraction] openField failed:', err);
      this.showError(`Connection error: ${err.message}`);
      if (btn) { btn.textContent = 'Open Field →'; btn.disabled = false; }
    }
  }

  private showError(msg: string) {
    if (!this.panel) return;
    // Remove any existing error
    document.getElementById('fp-error')?.remove();
    const el = document.createElement('div');
    el.id = 'fp-error';
    el.style.cssText = `
      padding: 10px 12px; margin: 8px 0; border-radius: 4px;
      background: rgba(209,79,79,0.10); border: 1px solid rgba(209,79,79,0.25);
      color: rgba(255,120,120,0.90); font-size: 11px; line-height: 1.5;
      font-family: 'Inter', sans-serif;
    `;
    el.textContent = msg;
    const btn = document.getElementById('fp-open-btn');
    btn?.parentNode?.insertBefore(el, btn);
  }

  private async closeField(learnings: string[]) {
    if (!this.currentField) return;
    try {
      const now = Date.now();
      await fetch(`${GATEWAY}/api/fields/${this.currentField.id}/status`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          status: 'complete',
          activationEnd: now,
          signalCount: this.signals.length,
          learnings,
        }),
      });
      this.currentField.status = 'complete';
      this.currentField.learnings = learnings;
      clearInterval(this.refreshTimer);
      // Remove field highlight from 3D field
      useAuraStore.getState().setActiveFieldCluster(null);
      this.render();
    } catch (err) {
      console.warn('[FieldInteraction] closeField failed:', err);
    }
  }

  private async pushToAgentMemory(learnings: string[]) {
    if (!this.currentCluster?.metadata?.agentStudioId || !GATEWAY) return;
    const agentId = this.currentCluster.metadata.agentStudioId;
    const fieldName = this.currentField?.name || 'Unnamed Field';

    for (const learning of learnings) {
      await fetch(`${GATEWAY}/api/ai/query`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          query: `push this field learning to agent ${agentId}: "${fieldName}" activation found: ${learning}`,
        }),
      }).catch(() => {});
    }
  }

  private async generateBrief(): Promise<string> {
    if (!this.currentCluster) return '';
    try {
      // Fetch real signals for this cluster
      const clusterRes = await fetch(`${GATEWAY}/api/clusters/${this.currentCluster.id}`, {
        signal: AbortSignal.timeout(5000),
      }).catch(() => null);
      const clusterData = clusterRes?.ok ? await clusterRes.json() : null;
      const signals = clusterData?.signals?.slice(0, 6) || [];

      const res = await fetch(`${GATEWAY}/api/ai/copy-brief`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          cluster: {
            id: this.currentCluster.id,
            topic: this.currentCluster.topic,
            description: this.currentCluster.description || '',
            entities: this.currentCluster.entities || [],
          },
          signals,
          format: 'field',
        }),
        signal: AbortSignal.timeout(20_000),
      });
      if (!res.ok) return '';
      const data = await res.json() as any;
      return data.brief || '';
    } catch { return ''; }
  }

  // ── Rendering ──────────────────────────────────────────────────────────────

  private render() {
    if (!this.panel) return;

    if (!this.currentCluster) {
      this.renderClusterPicker();
      return;
    }

    const status = this.currentField?.status || 'new';

    if (status === 'designing' || status === 'new') {
      this.renderDesigning();
    } else if (status === 'live') {
      this.renderLive();
    } else {
      this.renderComplete();
    }
  }

  private renderClusterPicker() {
    if (!this.panel) return;
    this.panel.innerHTML = `
      <div class="field-panel-inner">
        <div class="fp-header">
          <div class="fp-title">Open a Field</div>
        </div>
        <p class="fp-framing">Choose a cluster to ground your activation in real intelligence. High-signal clusters with recent momentum are the strongest foundations — they have more to say.</p>
        <div id="fp-cluster-list" class="fp-cluster-list">
          <div class="fp-loading">Loading clusters…</div>
        </div>
      </div>
    `;
    this.loadClusterList();
  }

  private async loadClusterList() {
    const el = document.getElementById('fp-cluster-list');
    if (!el) return;
    try {
      const res = await fetch(`${GATEWAY}/api/clusters?limit=30`);
      const { clusters } = await res.json() as any;
      // Sort by signal count × confidence — strongest foundation first
      const sorted = (clusters || []).sort((a: any, b: any) => {
        const scoreA = (a.signalIds?.length || 0) * (a.confidence || 0.5);
        const scoreB = (b.signalIds?.length || 0) * (b.confidence || 0.5);
        return scoreB - scoreA;
      });
      el.innerHTML = sorted.map((c: any) => {
        const signals = (c.signalIds || []).length;
        const conf = Math.round((c.confidence || 0) * 100);
        return `
        <div class="fp-cluster-item" data-id="${c.id}">
          <div class="fp-cluster-name">${c.topic}</div>
          <div class="fp-cluster-meta">${signals} signals · ${conf}% confidence</div>
        </div>`;
      }).join('');
      el.querySelectorAll('.fp-cluster-item').forEach(item => {
        (item as HTMLElement).onclick = () => this.loadCluster(item.getAttribute('data-id')!);
      });
    } catch {
      el.innerHTML = '<div class="fp-loading">Failed to load clusters</div>';
    }
  }

  private renderDesigning() {
    if (!this.panel || !this.currentCluster) return;
    const c = this.currentCluster;
    const modelsHtml = PARTICIPATION_MODELS.map(m => `
      <div class="fp-model-option" data-pm="${m.id}">
        <div class="fp-model-check"></div>
        <div>
          <div class="fp-model-label">${m.label}</div>
          <div class="fp-model-desc">${m.description}</div>
        </div>
      </div>
    `).join('');

    const signalCount = (c.signalIds || []).length;
    this.panel.innerHTML = `
      <div class="field-panel-inner">
        <div class="fp-header">
          <div class="fp-cluster-chip">${c.topic}</div>
          <div class="fp-title">Design the Field</div>
          <div class="fp-subtitle">${signalCount} signal${signalCount !== 1 ? 's' : ''} from web and news — grouped by shared entities and themes.</div>
        </div>
        <div class="fp-section">
          <div class="fp-label">Field name</div>
          <input id="fp-name" class="fp-input" placeholder="e.g. Sound &amp; Space" maxlength="60">
        </div>
        <div class="fp-section">
          <div class="fp-label">Intelligence brief <span class="fp-gen-link" id="fp-gen-brief">Generate from cluster →</span></div>
          <textarea id="fp-brief" class="fp-textarea" placeholder="What does this cluster's intelligence tell us about the activation opportunity?" rows="3"></textarea>
        </div>
        <div class="fp-section">
          <div class="fp-label">Participation model</div>
          <div class="fp-models">${modelsHtml}</div>
        </div>
        <button id="fp-open-btn" class="fp-btn fp-btn-primary" style="opacity:0.55">Open Field →</button>
        <div class="fp-back" id="fp-back">← Different cluster</div>
      </div>
    `;

    const nameEl = document.getElementById('fp-name') as HTMLInputElement;
    const briefEl = document.getElementById('fp-brief') as HTMLTextAreaElement;
    const openBtn = document.getElementById('fp-open-btn') as HTMLButtonElement;

    let selectedPm = '';

    nameEl.addEventListener('input', () => {
      // Subtle opacity change to show button is becoming active
      openBtn.style.opacity = nameEl.value.trim() && selectedPm ? '1' : '0.55';
    });

    // Use event delegation on the models container — more reliable than per-element onclick
    const modelsContainer = document.querySelector('.fp-models') as HTMLElement;
    if (modelsContainer) {
      modelsContainer.addEventListener('click', (e) => {
        const opt = (e.target as HTMLElement).closest('.fp-model-option') as HTMLElement | null;
        if (!opt) return;
        modelsContainer.querySelectorAll('.fp-model-option').forEach(o => o.classList.remove('selected'));
        opt.classList.add('selected');
        selectedPm = opt.getAttribute('data-pm') || '';
        openBtn.style.opacity = nameEl.value.trim() ? '1' : '0.55';
      });
    }

    document.getElementById('fp-gen-brief')!.onclick = async () => {
      const genEl = document.getElementById('fp-gen-brief')!;
      genEl.textContent = 'Writing brief…';
      genEl.style.opacity = '0.5';
      genEl.style.pointerEvents = 'none';
      // Stream the brief into the textarea as it generates
      try {
        const brief = await this.generateBrief();
        if (brief && briefEl) {
          // Typewriter reveal
          briefEl.value = '';
          const words = brief.split(' ');
          for (let i = 0; i < words.length; i++) {
            briefEl.value += (i > 0 ? ' ' : '') + words[i];
            await new Promise(r => setTimeout(r, 30));
          }
        }
      } finally {
        genEl.textContent = 'Regenerate →';
        genEl.style.opacity = '';
        genEl.style.pointerEvents = '';
      }
    };

    openBtn.onclick = () => {
      if (!nameEl.value.trim()) {
        nameEl.focus();
        nameEl.style.borderColor = 'rgba(209,79,79,0.5)';
        setTimeout(() => { nameEl.style.borderColor = ''; }, 2000);
        return;
      }
      if (!selectedPm) {
        const models = document.querySelector('.fp-models') as HTMLElement;
        if (models) {
          models.style.outline = '1px solid rgba(209,79,79,0.4)';
          models.style.borderRadius = '4px';
          setTimeout(() => { models.style.outline = ''; }, 2000);
        }
        return;
      }
      this.openField(nameEl.value.trim(), selectedPm, briefEl.value.trim());
    };

    document.getElementById('fp-back')!.onclick = () => {
      this.currentCluster = null;
      this.currentField = null;
      this.renderClusterPicker();
    };
  }

  private renderLive() {
    if (!this.panel || !this.currentField) return;
    const f = this.currentField;
    const duration = f.activationStart ? Math.floor((Date.now() - f.activationStart) / 60000) : 0;
    const health = HEALTH_STATES.find(h => h.id === this.healthState) || HEALTH_STATES[0];

    this.panel.innerHTML = `
      <div class="field-panel-inner">
        <div class="fp-header">
          <div class="fp-live-dot"></div>
          <div class="fp-title">${f.name}</div>
          <div class="fp-subtitle">${this.currentCluster?.topic || ''} · ${duration}m live</div>
        </div>

        <div class="fp-health">
          <div class="fp-health-dot" style="background:${health.color}"></div>
          <div>
            <div class="fp-health-label" style="color:${health.color}">${health.label}</div>
            <div class="fp-health-desc">${health.description}</div>
          </div>
          <select id="fp-health-select" class="fp-select">
            ${HEALTH_STATES.map(h => `<option value="${h.id}" ${h.id === this.healthState ? 'selected' : ''}>${h.label}</option>`).join('')}
          </select>
        </div>

        <div class="fp-section">
          <div class="fp-label">Field intelligence</div>
          <div id="fp-intelligence" class="fp-intelligence">
            <div class="fp-loading">Analyzing signals…</div>
          </div>
        </div>

        <div class="fp-section">
          <div class="fp-label" style="display:flex;justify-content:space-between">
            <span>Incoming signals</span>
            <span class="fp-muted" id="fp-signal-count">${this.signals.length}</span>
          </div>
          <div id="fp-signals" class="fp-signals">
            ${this.signals.length === 0 ? '<div class="fp-loading">Pulling signals…</div>' : ''}
          </div>
        </div>

        <button class="fp-btn fp-btn-secondary" id="fp-ingest-btn">Pull fresh signals</button>
        <button id="fp-close-btn" class="fp-btn fp-btn-primary" style="margin-top:8px">Close & Capture Learnings →</button>
      </div>
    `;

    this.renderSignalFeed();
    this.renderIntelligence();

    document.getElementById('fp-health-select')!.onchange = (e) => {
      this.healthState = (e.target as HTMLSelectElement).value;
    };

    document.getElementById('fp-ingest-btn')!.onclick = async () => {
      const btn = document.getElementById('fp-ingest-btn') as HTMLButtonElement;
      btn.textContent = 'Pulling…'; btn.disabled = true;
      await fetch(`${GATEWAY}/api/ingestion/trigger`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ source: 'web', query: this.currentCluster?.topic || '' }),
      }).catch(() => {});
      setTimeout(async () => {
        await this.fetchSignals();
        btn.textContent = 'Pull fresh signals'; btn.disabled = false;
        this.renderIntelligence();
      }, 6000);
    };

    document.getElementById('fp-close-btn')!.onclick = () => this.renderLearningsCapture();
  }

  private async renderIntelligence() {
    const el = document.getElementById('fp-intelligence');
    if (!el || !GATEWAY || this.signals.length === 0) {
      if (el) el.innerHTML = '<div class="fp-intelligence-empty">Signals will appear here as they arrive.</div>';
      return;
    }

    el.innerHTML = '<div class="fp-loading">Reading signals…</div>';

    try {
      const cluster = this.currentCluster;
      const neonId = cluster?.id || '';
      const res = await fetch(`${GATEWAY}/api/ai/copy-brief`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          cluster: { id: neonId, topic: cluster?.topic || '', description: cluster?.description || '', entities: cluster?.entities || [] },
          signals: this.signals.slice(0, 8),
          format: 'digest',
        }),
        signal: AbortSignal.timeout(18_000),
      });

      if (!res.ok) throw new Error('copy-brief failed');
      const data = await res.json() as any;
      const brief = data.brief || '';

      // Render as bullet points if the copy agent returned bullets
      const lines = brief.split('\n').filter((l: string) => l.trim());
      el.innerHTML = lines.map((line: string) => {
        const clean = line.replace(/^[-•·→*]\s*/, '');
        return `<div class="fp-intelligence-point">${clean}</div>`;
      }).join('');
    } catch {
      el.innerHTML = `<div class="fp-intelligence-empty">${this.signals.length} signals captured. Intelligence analysis unavailable.</div>`;
    }
  }

  private renderSignalFeed() {
    const el = document.getElementById('fp-signals');
    const countEl = document.getElementById('fp-signal-count');
    if (!el) return;
    if (countEl) countEl.textContent = String(this.signals.length);
    el.innerHTML = this.signals.slice(0, 8).map(s => `
      <div class="fp-signal">
        <div class="fp-signal-content">${(s.content || '—').slice(0, 85)}</div>
        <div class="fp-signal-meta">${s.source || 'web'} · ${Math.round((s.confidence || 0) * 100)}% · ${timeAgo(s.created_at || Date.now())}</div>
      </div>
    `).join('') || '<div class="fp-loading">No signals yet</div>';
  }

  private renderLearningsCapture() {
    if (!this.panel) return;
    const sigCount = this.signals.length;
    const duration = this.currentField?.activationStart
      ? Math.floor((Date.now() - this.currentField.activationStart) / 60000)
      : 0;

    this.panel.innerHTML = `
      <div class="field-panel-inner">
        <div class="fp-header">
          <div class="fp-title">What did you learn?</div>
          <div class="fp-subtitle">${duration}m live · ${sigCount} signals captured</div>
        </div>
        <p class="fp-framing">These learnings become permanent memory for the <strong>${this.currentCluster?.topic || ''}</strong> cluster agent. The next time you work in this domain, the agent will remember what worked and what didn't.</p>
        <div class="fp-section">
          <div class="fp-label">Key observations — one per line</div>
          <textarea id="fp-learnings" class="fp-textarea" rows="5" placeholder="The crowd engaged most with...&#10;The timing that worked was...&#10;What surprised us was...&#10;Do differently next time:..."></textarea>
        </div>
        <button id="fp-complete-btn" class="fp-btn fp-btn-primary">Close Field & Save →</button>
      </div>
    `;

    document.getElementById('fp-complete-btn')!.onclick = async () => {
      const text = (document.getElementById('fp-learnings') as HTMLTextAreaElement).value;
      const learnings = text.split('\n').map(l => l.trim()).filter(Boolean);
      await this.closeField(learnings);
    };
  }

  private renderComplete() {
    if (!this.panel || !this.currentField) return;
    const f = this.currentField;
    const learnings: string[] = f.learnings || [];
    const duration = f.activationStart && f.activationEnd
      ? Math.floor((f.activationEnd - f.activationStart) / 60000)
      : null;

    this.panel.innerHTML = `
      <div class="field-panel-inner">
        <div class="fp-header">
          <div class="fp-complete-badge">COMPLETE</div>
          <div class="fp-title">${f.name}</div>
          ${duration !== null ? `<div class="fp-subtitle">${duration}m · ${f.signalCount} signals captured</div>` : ''}
        </div>
        <div class="fp-section">
          <div class="fp-label">What you captured</div>
          ${learnings.length === 0
            ? '<div class="fp-muted" style="font-style:italic">No learnings recorded for this field.</div>'
            : learnings.map(l => `<div class="fp-learning-item">→ ${l}</div>`).join('')}
        </div>
        <div class="fp-section">
          <button id="fp-push-btn" class="fp-btn fp-btn-primary">Send to ${this.currentCluster?.topic || 'cluster'} agent →</button>
          <p class="fp-framing" style="margin-top:10px">These observations become persistent memory. The next time you work in <strong>${this.currentCluster?.topic || 'this domain'}</strong>, the cluster agent will recall what worked here and build on it.</p>
        </div>
        <div class="fp-back" id="fp-new-field">Open another field</div>
      </div>
    `;

    document.getElementById('fp-push-btn')!.onclick = async () => {
      const btn = document.getElementById('fp-push-btn') as HTMLButtonElement;
      btn.textContent = 'Pushing to agent…';
      await this.pushToAgentMemory(learnings);
      btn.textContent = '✓ Memory updated';
      btn.disabled = true;
    };

    document.getElementById('fp-new-field')!.onclick = () => {
      this.currentField = null;
      this.renderDesigning();
    };
  }

  // ── Public: called when a cluster node is selected in the field ─────────────

  onClusterSelected(clusterId: string) {
    if (useAuraStore.getState().mode === 'field') {
      this.loadCluster(clusterId);
    }
  }

  private panelVisible = false;

  update(_dt: number) {
    const mode = useAuraStore.getState().mode;
    const shouldShow = mode === 'field';

    if (shouldShow !== this.panelVisible) {
      this.panelVisible = shouldShow;
      const el = document.getElementById('field-panel');
      if (el) {
        if (shouldShow) {
          el.classList.add('visible');
          this.render();
        } else {
          el.classList.remove('visible');
          this.onExit();
        }
      }
    }
  }

  dispose() {
    clearInterval(this.refreshTimer);
    document.getElementById('field-panel')?.classList.remove('visible');
    useAuraStore.getState().setActiveFieldCluster(null);
  }
}
